import { Injectable } from '@angular/core';
import { SciChartSurface } from 'scichart/Charting/Visuals/SciChartSurface';
import type { DomToImage } from 'dom-to-image';
import * as _domtoimage from 'dom-to-image';

// @types/dom-to-image declares a named `DomToImage` export that doesn't exist
// at runtime (the CJS module.exports IS the {toPng, …} object directly).
const domtoimage = _domtoimage as unknown as DomToImage;

/**
 * Export capture at 2× scale for 300 DPI print quality.
 * A PDF content area is ~7.5 inches wide; 7.5 × 300 = 2250 px.
 * We render at 2400×1200 CSS px so SciChart's WebGL canvas is large enough,
 * then capture at 2× pixel ratio for a final 4800×2400 image.
 */
const EXPORT_CSS_WIDTH = 2400;
const EXPORT_CSS_HEIGHT = 1200;
const EXPORT_PIXEL_RATIO = 2;

@Injectable({ providedIn: 'root' })
export class ChartCaptureService {
  private divIds = new Map<string, string>();
  private snapshots = new Map<string, string>();
  private renderHandlers = new Map<string, () => void>();
  private surfaces = new Map<string, SciChartSurface>();
  private resizing = false;

  register(name: string, surface: SciChartSurface, divId: string): void {
    this.divIds.set(name, divId);
    this.surfaces.set(name, surface);
    this.snapshots.delete(name);

    const handler = () => {
      if (!this.resizing) {
        this.captureDiv(name, divId);
      }
    };
    surface.rendered.subscribe(handler);
    this.renderHandlers.set(name, handler);
    console.log(`ChartCapture: registered "${name}" → #${divId}`);
  }

  unregister(name: string, surface: SciChartSurface): void {
    const handler = this.renderHandlers.get(name);
    if (handler) {
      surface.rendered.unsubscribe(handler);
    }
    this.renderHandlers.delete(name);
    this.divIds.delete(name);
    this.surfaces.delete(name);
  }

  captureAsBase64(name: string): string | null {
    return this.snapshots.get(name) ?? null;
  }

  /**
   * Capture all requested charts at print quality for PDF export.
   * Charts still in the DOM are resized to a large off-screen container
   * and captured at 2× pixel ratio. Otherwise the cached snapshot is used.
   */
  async captureAllForExport(names: string[]): Promise<Record<string, string>> {
    const result: Record<string, string> = {};

    for (const name of names) {
      const divId = this.divIds.get(name);
      const surface = this.surfaces.get(name);
      const node = divId ? document.getElementById(divId) : null;

      if (node && surface) {
        const b64 = await this.captureAtSize(
          name, node, surface,
          EXPORT_CSS_WIDTH, EXPORT_CSS_HEIGHT, EXPORT_PIXEL_RATIO
        );
        if (b64) {
          result[name] = b64;
          continue;
        }
      }

      const cached = this.snapshots.get(name);
      if (cached) {
        result[name] = cached;
      } else {
        console.warn(`ChartCapture: no snapshot for "${name}" — was that tab visited?`);
      }
    }

    console.log(
      `ChartCapture: captureAllForExport returning ${Object.keys(result).length}/${names.length} images`,
      Object.keys(result)
    );
    return result;
  }

  /**
   * Resize → re-render → capture at high resolution → restore.
   *
   * The trick for pixel-ratio scaling: we render the SciChart canvas at
   * cssWidth × cssHeight CSS pixels, then draw that into a canvas that is
   * (cssWidth × ratio) × (cssHeight × ratio) physical pixels. This gives
   * us a sharp, print-ready image without relying on devicePixelRatio.
   */
  private async captureAtSize(
    name: string,
    node: HTMLElement,
    surface: SciChartSurface,
    cssWidth: number,
    cssHeight: number,
    ratio: number
  ): Promise<string | null> {
    const orig = {
      width: node.style.width,
      height: node.style.height,
      flex: node.style.flex,
      position: node.style.position,
      zIndex: node.style.zIndex,
      overflow: node.style.overflow,
      left: node.style.left,
      top: node.style.top,
    };

    try {
      this.resizing = true;

      // Move off-screen and set to export dimensions
      node.style.position = 'fixed';
      node.style.left = '-99999px';
      node.style.top = '0px';
      node.style.zIndex = '-9999';
      node.style.overflow = 'hidden';
      node.style.flex = 'none';
      node.style.width = `${cssWidth}px`;
      node.style.height = `${cssHeight}px`;

      surface.invalidateElement();
      await this.waitForRender(surface);
      // Extra frame for browser layout + paint
      await new Promise(r => requestAnimationFrame(() => setTimeout(r, 80)));

      const b64 = await this.captureNodeHiRes(node, cssWidth, cssHeight, ratio);
      return b64;
    } catch (err) {
      console.warn(`ChartCapture: high-res capture failed for "${name}"`, err);
      return null;
    } finally {
      node.style.width = orig.width;
      node.style.height = orig.height;
      node.style.flex = orig.flex;
      node.style.position = orig.position;
      node.style.left = orig.left;
      node.style.top = orig.top;
      node.style.zIndex = orig.zIndex;
      node.style.overflow = orig.overflow;
      surface.invalidateElement();
      this.resizing = false;
    }
  }

  /**
   * Capture a DOM node at a higher-than-1× pixel ratio.
   * dom-to-image renders at 1:1 CSS px, so we first get a 1× image,
   * then paint it scaled onto a larger canvas.
   */
  private async captureNodeHiRes(
    node: HTMLElement,
    cssWidth: number,
    cssHeight: number,
    ratio: number
  ): Promise<string | null> {
    const dataUrl = await domtoimage.toPng(node, {
      bgcolor: '#1d1d1d',
      width: cssWidth,
      height: cssHeight,
    });

    if (ratio <= 1) {
      return dataUrl.split(',')[1] || null;
    }

    // Scale up to final print resolution
    const img = new Image();
    img.src = dataUrl;
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
    });

    const canvas = document.createElement('canvas');
    canvas.width = cssWidth * ratio;
    canvas.height = cssHeight * ratio;
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const hiResUrl = canvas.toDataURL('image/png');
    return hiResUrl.split(',')[1] || null;
  }

  private waitForRender(surface: SciChartSurface): Promise<void> {
    return new Promise(resolve => {
      const cb = () => {
        surface.rendered.unsubscribe(cb);
        resolve();
      };
      surface.rendered.subscribe(cb);
    });
  }

  /** On-screen cache capture (used as fallback if chart is navigated away). */
  private captureDiv(name: string, divId: string): void {
    const node = document.getElementById(divId);
    if (!node) return;

    domtoimage
      .toPng(node, { bgcolor: '#1d1d1d' })
      .then((dataUrl: string) => {
        const b64 = dataUrl.split(',')[1];
        if (b64) {
          this.snapshots.set(name, b64);
        }
      })
      .catch((err: unknown) => {
        console.warn(`ChartCapture: capture failed for "${name}"`, err);
      });
  }
}
